import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import MyAccountQuery from 'Query/MyAccount.query';
import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    MyAccountAddressTableContainer as SourceMyAccountAddressTableContainer
} from 'SourceComponent/MyAccountAddressTable/MyAccountAddressTable.container';
import { showNotification } from 'Store/Notification/Notification.action';
import { CustomerType } from 'Type/Account.type';
import { getAddressNotificationMessage, trimCustomerAddress } from 'Util/Address';
import { isSignedIn } from 'Util/Auth';
import { fetchMutation, getErrorMessage } from 'Util/Request';

export const MyAccountDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/MyAccount/MyAccount.dispatcher'
);

/** @namespace PeggysagePwa/Component/MyAccountAddressTable/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    customer: state.MyAccountReducer.customer
});

/** @namespace PeggysagePwa/Component/MyAccountAddressTable/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    showErrorNotification: (error) => dispatch(showNotification('error', getErrorMessage(error))),
    showSuccessNotification: (message) => dispatch(showNotification('success', message)),
    updateCustomerDetails: () => MyAccountDispatcher.then(
        ({ default: dispatcher }) => dispatcher.requestCustomerData(dispatch)
    )
});

/** @namespace PeggysagePwa/Component/MyAccountAddressTable/Container */
export class MyAccountAddressTableContainer extends SourceMyAccountAddressTableContainer {
    static propTypes = {
        ...SourceMyAccountAddressTableContainer.propTypes,
        customer: CustomerType.isRequired,
        showErrorNotification: PropTypes.func.isRequired,
        showSuccessNotification: PropTypes.func.isRequired,
        updateCustomerDetails: PropTypes.func.isRequired,
        showQuickEdit: PropTypes.bool
    };

    static defaultProps = {
        ...SourceMyAccountAddressTableContainer.defaultProps,
        showQuickEdit: false
    };

    containerFunctions = {
        ...this.containerFunctions,
        onChooseAsDefaultShippingClick: this.onChooseAsDefaultShippingClick.bind(this),
        onChooseAsDefaultBillingClick: this.onChooseAsDefaultBillingClick.bind(this)
    };

    state = {
        isLoading: false
    };

    containerProps() {
        const { showQuickEdit, customer: { can_change_billing_address = true } = {} } = this.props;
        const { isLoading } = this.state;

        return {
            ...super.containerProps(),
            showQuickEdit,
            isLoading,
            canChangeBillingAddress: can_change_billing_address
        };
    }

    async handleAfterAction(status, operation) {
        const {
            updateCustomerDetails,
            showErrorNotification,
            showSuccessNotification
        } = this.props;

        try {
            await updateCustomerDetails();
            this.setState({ isLoading: false });
            const message = getAddressNotificationMessage(operation);
            showSuccessNotification(message);
        } catch (e) {
            this.setState({ isLoading: false });
            showErrorNotification(e);
        }
    }

    handleError(error) {
        const { showErrorNotification } = this.props;

        showErrorNotification(error);
        this.setState({ isLoading: false });
    }

    /**
     * @param {string} key ('default_shipping'||'default_billing')
     * @returns {Promise<void>}
     */
    async handleEditDefaultParam(key) {
        const { address, address: { id } } = this.props;

        if (!isSignedIn()) {
            return;
        }

        const editedAddress = trimCustomerAddress({
            ...address,
            [key]: true
        });

        const query = MyAccountQuery.getUpdateAddressMutation(id, editedAddress);

        this.setState({ isLoading: true });

        try {
            await fetchMutation(query);
            this.handleAfterAction('success', 'edited');
        } catch (err) {
            this.handleError(err);
        }
    }

    onChooseAsDefaultShippingClick() {
        this.handleEditDefaultParam('default_shipping');
    }

    onChooseAsDefaultBillingClick() {
        this.handleEditDefaultParam('default_billing');
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MyAccountAddressTableContainer);
